import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { API_URLS } from "../../constants/api-urls";
import "./ProformaInvoice.scss";
import LinkedLogisticsPanel from "../../components/moduleLogistics/LinkedLogisticsPanel";

const QA_FEE_PERCENT = 0.25;

/** Parse fee breakdown from QA quotation additional_notes (same logic as buyer_qa_quotations_list). */
function parseQAFeeBreakdown(additionalNotes) {
  if (!additionalNotes || typeof additionalNotes !== "string") return null;
  const line = additionalNotes.split("\n")[0] || "";
  if (!line.includes("Professional fee:") || !line.includes("Subtotal:")) return null;
  const result = { baseInspectionFee: 0, travelLogistics: 0, regionalTaxes: 0, platformEscrowFee: 0, subtotal: 0, totalWithFee: 0 };
  const patterns = [
    { key: "baseInspectionFee", regex: /Professional fee:\s*([\d.]+)/i },
    { key: "travelLogistics", regex: /Travel\s*[&]\s*logistics:\s*([\d.]+)/i },
    { key: "regionalTaxes", regex: /Taxes:\s*([\d.]+)/i },
    { key: "subtotal", regex: /Subtotal:\s*([\d.]+)/i },
    { key: "totalWithFee", regex: /Total\s*\(incl[^)]*\):\s*([\d.]+)/i },
  ];
  patterns.forEach(({ key, regex }) => {
    const m = line.match(regex);
    if (m) result[key] = parseFloat(m[1]) || 0;
  });
  result.platformEscrowFee = Math.round(result.subtotal * QA_FEE_PERCENT * 100) / 100;
  return result;
}

export default function ProformaInvoice() {
  const { auctionId, inspectionId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [p2pPayment, setP2pPayment] = useState(null);

  const isQAInvoice = Boolean(inspectionId);

  useEffect(() => {
    if (inspectionId) {
      fetchQAInspectionInvoice();
    } else if (auctionId) {
      fetchInvoiceAndAuction();
    } else {
      setLoading(false);
    }
  }, [auctionId, inspectionId]);

  const fetchQAInspectionInvoice = async () => {
    try {
      setLoading(true);
      const inspectionRes = await CommonService.get(API_URLS.QA.INSPECTION_BY_ID(inspectionId));
      const inspection = inspectionRes?.data ?? null;
      if (!inspection) {
        toast.error("Inspection not found");
        setLoading(false);
        return;
      }
      const qaRequestRes = await CommonService.get(API_URLS.QA.GET_BY_ID(inspection.qa_request_id));
      const qaRequest = qaRequestRes?.data ?? null;
      let buyerCompany = {};
      let inspectorProfile = {};
      try {
        const buyerRes = await CommonService.get(`/company/profile/${qaRequest?.requested_by_user_id}`);
        buyerCompany = buyerRes?.data ?? buyerRes ?? {};
      } catch (_) {}
      try {
        const qipRes = await CommonService.get(API_URLS.QA.INSPECTOR_PROFILE_BY_USER(inspection.inspector_user_id));
        inspectorProfile = qipRes?.data ?? qipRes ?? {};
      } catch (_) {
        try {
          const fallback = await CommonService.get(`/company/profile/${inspection.inspector_user_id}`);
          inspectorProfile = fallback?.data ?? fallback ?? {};
        } catch (_) {}
      }
      // Final payable: same as buyer QA quotations list (breakdown totalWithFee or inspection_fee)
      let finalPayable = parseFloat(inspection.inspection_fee) || 0;
      try {
        const quotRes = await CommonService.get(API_URLS.QA.QUOTATIONS_BY_REQUEST(inspection.qa_request_id));
        const quoteList = quotRes?.data && Array.isArray(quotRes.data) ? quotRes.data : [];
        const quotation = quoteList.find((q) => q.quotation_id === inspection.quotation_id);
        if (quotation?.additional_notes) {
          const breakdown = parseQAFeeBreakdown(quotation.additional_notes);
          if (breakdown?.totalWithFee != null && breakdown.totalWithFee > 0) {
            finalPayable = breakdown.totalWithFee;
          }
        }
      } catch (_) {}
      const currency = inspection.currency || "USD";
      const buyerAddress = buyerCompany.address && typeof buyerCompany.address === "object"
        ? buyerCompany.address
        : buyerCompany.address || null;
      const buyerGst = buyerCompany.tax_number || buyerCompany.gstin || null;
      const inspectorName = inspectorProfile.company_name || inspectorProfile.name || "Quality Inspector";
      const inspectorAddress = typeof inspectorProfile.address === "string"
        ? inspectorProfile.address
        : inspectorProfile.address && typeof inspectorProfile.address === "object"
          ? inspectorProfile.address
          : null;
      const inspectorGst = inspectorProfile.gst_no || inspectorProfile.tax_number || inspectorProfile.gstin || null;
      const invoicePayload = {
        invoice_number: `QA-INV-${inspectionId}`,
        invoice_date: new Date().toISOString().split("T")[0],
        subtotal: finalPayable,
        gst_amount: 0,
        total_amount: finalPayable,
        unit_price: finalPayable,
        quantity: 1,
        currency: currency,
        product_description: "Quality inspection service (QA quotation approved)",
        buyer_company: buyerCompany.company_name || buyerCompany.name || "Buyer",
        buyer_company_address: buyerAddress,
        buyer_gstin: buyerGst,
        seller_company: inspectorName,
        seller_company_address: inspectorAddress,
        seller_gstin: inspectorGst,
      };
      const auctionPayload = {
        product_name: "QA Inspection Service",
        product_description: "Quality inspection as per approved quotation",
        quantity: 1,
        seller_company: inspectorName,
        address: inspectorAddress,
        gst_rate: 0,
      };
      setInvoice(invoicePayload);
      setAuction(auctionPayload);
      setP2pPayment(inspection.p2p_payment ?? null);
    } catch (error) {
      console.error("Error fetching QA inspection invoice:", error);
      toast.error("Failed to load invoice details");
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceAndAuction = async () => {
    try {
      setLoading(true);
      
      // Fetch auction details
      const auctionResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`);
      if (auctionResponse && auctionResponse.success) {
        setAuction(auctionResponse.data);
      }

      // Try to fetch existing invoice
      try {
        const invoiceResponse = await CommonService.get(`${API_URLS.AUCTION.PROFORMA_INVOICE(auctionId)}`);
        if (invoiceResponse && invoiceResponse.success && invoiceResponse.data) {
          setInvoice(invoiceResponse.data);
        }
      } catch (error) {
        // Invoice doesn't exist, will generate it
        console.log("Invoice not found, will generate");
      }
    } catch (error) {
      console.error("Error fetching invoice and auction:", error);
      toast.error("Failed to load invoice details");
    } finally {
      setLoading(false);
    }
  };

  const generateInvoice = async () => {
    try {
      setGenerating(true);
      const response = await CommonService.post(`${API_URLS.AUCTION.PROFORMA_INVOICE(auctionId)}`, {});
      
      if (response && response.success) {
        toast.success("Proforma invoice generated successfully!");
        // Fetch the generated invoice
        const invoiceResponse = await CommonService.get(`${API_URLS.AUCTION.PROFORMA_INVOICE(auctionId)}`);
        if (invoiceResponse && invoiceResponse.success && invoiceResponse.data) {
          setInvoice(invoiceResponse.data);
        }
      }
    } catch (error) {
      console.error("Error generating invoice:", error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || "Failed to generate invoice";
      toast.error(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    // Auto-generate invoice if it doesn't exist
    if (!loading && !invoice && auction && auction.seller_decision === 'accepted') {
      generateInvoice();
    }
  }, [loading, invoice, auction]);

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) {
      return invoice?.currency === "USD" ? "$0" : "₹0";
    }
    const currencyCode = invoice?.currency || "INR";
    return new Intl.NumberFormat(currencyCode === "USD" ? "en-US" : "en-IN", {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(numAmount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatAddress = (addressData) => {
    if (!addressData) return "N/A";
    
    try {
      // Parse if it's a JSON string
      let address = typeof addressData === 'string' 
        ? JSON.parse(addressData) 
        : addressData;
      
      // Build address parts
      const addressParts = [];
      
      if (address.address) addressParts.push(address.address);
      if (address.landmark && address.landmark !== "N/A") addressParts.push(address.landmark);
      
      const locationParts = [];
      if (address.city) locationParts.push(address.city);
      if (address.state) locationParts.push(address.state);
      if (address.pincode) locationParts.push(address.pincode);
      if (address.country && address.country !== "India") locationParts.push(address.country);
      
      if (locationParts.length > 0) {
        addressParts.push(locationParts.join(", "));
      }
      
      return addressParts.length > 0 ? addressParts.join(", ") : "N/A";
    } catch (error) {
      // If parsing fails, return as is or try to use it as a string
      return typeof addressData === 'string' ? addressData : "N/A";
    }
  };

  const isBuyer = () => {
    const user = StorageService.getUser();
    if (!user) return false;
    
    // Check user_type_id: 1 = BUYER, 2 = SELLER, 3 = BOTH
    const userTypeId = String(user.user_type_id || '').trim();
    const typeIds = userTypeId ? userTypeId.split(',').map(id => String(id).trim()) : [];
    
    // Check if user_type_id includes '1' (Buyer) or '3' (Both)
    const isBuyerByUserType = typeIds.includes('1') || 
                              typeIds.includes('3') || 
                              userTypeId === '1' || 
                              userTypeId === '3';
    
    // Also check company_role from user object
    const companyRole = user.company_role || '';
    const isBuyerByRole = companyRole === 'BUYER' || companyRole === 'BOTH';
    
    return isBuyerByUserType || isBuyerByRole;
  };

  const getViewerUserId = () => {
    const user = StorageService.getUser();
    if (!user) return null;
    const id = Number(user.user_login_id ?? user.id);
    return Number.isFinite(id) && id > 0 ? id : null;
  };

  const handleDownloadInvoice = () => {
    // TODO: Implement PDF download
    toast.info("Download functionality coming soon");
  };

  const handleEmailToBuyer = async () => {
    if (!invoice) {
      toast.error("Invoice not found");
      return;
    }
    if (isQAInvoice) {
      toast.info("Email option not available for QA invoice");
      return;
    }

    try {
      setSendingEmail(true);
      const response = await CommonService.post(API_URLS.AUCTION.SEND_PROFORMA_INVOICE_EMAIL(auctionId), {});
      
      if (response && response.success) {
        toast.success("Invoice sent to buyer successfully!");
        // Redirect to my auctions page
        navigate("/auction/my/auctions");
      } else {
        throw new Error(response?.message || 'Failed to send invoice email');
      }
    } catch (error) {
      console.error("Error sending email:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to send email";
      toast.error(errorMessage);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleProceedToPayment = () => {
    if (isQAInvoice) {
      const inspectorName = invoice?.seller_company || auction?.seller_company || "Quality Inspector";
      const totalQuote = invoice?.total_amount ?? invoice?.subtotal ?? 0;
      navigate(`/qa/inspection/${inspectionId}/payment`, {
        state: {
          inspectorName,
          inspectorRoleHub: "Lead Inspector",
          totalQuote,
          currency: invoice?.currency || "USD",
        },
      });
    } else {
      navigate(`/auction/${auctionId}/payment/process`, {
        state: { fromProformaInvoice: true, invoice, auctionId },
      });
    }
  };

  const handleBack = () => {
    if (isQAInvoice) {
      navigate("/buyer/qa-quotations-list");
    } else {
      navigate(-1);
    }
  };

  if (loading || generating) {
    return (
      <div className="proforma-invoice-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{generating ? "Generating invoice..." : "Loading..."}</p>
        </div>
      </div>
    );
  }

  if (!invoice || !auction) {
    return (
      <div className="proforma-invoice-page">
        <div className="error-container">
          <p>Invoice not found. Please try again.</p>
        </div>
      </div>
    );
  }

  // Calculate GST rate
  const gstRate = auction.gst_rate || 18;
  const gstPercentage = `${gstRate}%`;

  const viewerUserId = getViewerUserId();
  const sellerUserId = Number(invoice.seller_user_id ?? auction.user_login_id);
  const winningBuyerUserId = Number(invoice.buyer_user_id);
  const isAuctionSeller =
    viewerUserId != null &&
    Number.isFinite(sellerUserId) &&
    sellerUserId > 0 &&
    viewerUserId === sellerUserId;
  const isWinningBuyer =
    viewerUserId != null &&
    Number.isFinite(winningBuyerUserId) &&
    winningBuyerUserId > 0 &&
    viewerUserId === winningBuyerUserId;
  const showProceedToPayment = isQAInvoice
    ? isBuyer() && (!p2pPayment?.is_p2p_linked || p2pPayment?.can_show_payment_now === true)
    : isWinningBuyer;
  const showEmailToBuyer = !isQAInvoice && isAuctionSeller && !isWinningBuyer;
  const qaPaymentApprovalPending =
    isQAInvoice && p2pPayment?.is_p2p_linked && p2pPayment?.payment_approval_pending === true;
  const qaWaitingForInspector =
    isQAInvoice && p2pPayment?.is_p2p_linked && p2pPayment?.waiting_for_inspector_accept === true;

  return (
    <div className="proforma-invoice-page">
      <div className="invoice-container">
        <button className="back-btn" onClick={handleBack}>
          ← Back
        </button>

        {qaWaitingForInspector ? (
          <div className="qa-invoice-approval-banner" role="status">
            Waiting for the inspector to accept the assignment. Proceed to Payment will be enabled
            after acceptance
            {p2pPayment?.payment_approval_status === 'pending'
              ? ' and internal payment approval is complete'
              : ''}
            .
          </div>
        ) : null}

        {qaPaymentApprovalPending ? (
          <div className="qa-invoice-approval-banner" role="status">
            Payment approval is in progress. Proceed to Payment will be enabled once all approval
            levels are complete.
          </div>
        ) : null}

        {auctionId && (
          <LinkedLogisticsPanel
            sourceModule="auction"
            sourceRecordType="auction_award"
            sourceRecordId={String(auctionId)}
            linkPurpose="fulfillment"
            title="Commercial closure logistics"
          />
        )}

        {/* Header Section */}
        <div className="header-section">
          <div className="header-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <div className="header-content">
            <h1 className="page-title">Proforma Invoice Generated</h1>
            <p className="page-subtitle">Official invoice document for the transaction</p>
          </div>
        </div>

        {/* Invoice Card */}
        <div className="invoice-card">
          {/* Invoice Header */}
          <div className="invoice-header">
            <h2 className="invoice-title">PROFORMA INVOICE</h2>
            <div className="brand-badge">TROCANET</div>
          </div>

          {/* Invoice Details */}
          <div className="invoice-details">
            <div className="detail-row">
              <span className="detail-label">Invoice #:</span>
              <span className="detail-value">{invoice.invoice_number || "PI-UCTION-1"}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Date:</span>
              <span className="detail-value">{formatDate(invoice.invoice_date)}</span>
            </div>
          </div>

          <div className="divider"></div>

          {/* Parties Information */}
          <div className="parties-section">
            <div className="party-column">
              <div className="party-label">{isQAInvoice ? "From (Buyer)" : "From (Seller)"}</div>
              <div className="party-name">
                {isQAInvoice
                  ? (invoice.buyer_company || invoice.buyer_name || "N/A")
                  : (invoice.seller_company || auction.seller_company || auction.seller_name || "N/A")}
              </div>
              <div className="party-address">
                {formatAddress(
                  isQAInvoice
                    ? (invoice.buyer_company_address || invoice.buyer_address)
                    : (invoice.seller_company_address || invoice.seller_address || auction.address)
                )}
              </div>
              <div className="party-gst">
                GST: {isQAInvoice
                  ? (invoice.buyer_gstin_from_profile || invoice.buyer_gstin || "N/A")
                  : (invoice.seller_gstin_from_profile || invoice.seller_gstin || auction.seller_gstin || "N/A")}
              </div>
            </div>
            <div className="party-column">
              <div className="party-label">{isQAInvoice ? "To (QA)" : "To (Buyer)"}</div>
              <div className="party-name">
                {isQAInvoice
                  ? (invoice.seller_company || auction?.seller_company || "N/A")
                  : (invoice.buyer_company || invoice.buyer_name || "N/A")}
              </div>
              <div className="party-address">
                {formatAddress(
                  isQAInvoice
                    ? (invoice.seller_company_address || invoice.seller_address || auction?.address)
                    : (invoice.buyer_company_address || invoice.buyer_address)
                )}
              </div>
              <div className="party-gst">
                GST: {isQAInvoice
                  ? (invoice.seller_gstin_from_profile || invoice.seller_gstin || "N/A")
                  : (invoice.buyer_gstin_from_profile || invoice.buyer_gstin || "N/A")}
              </div>
            </div>
          </div>

          <div className="divider"></div>

          {/* Description Table */}
          <div className="description-table">
            <div className="table-header">
              <div className="header-cell description-cell">Description</div>
              <div className="header-cell quantity-cell">Quantity</div>
              <div className="header-cell amount-cell">Amount</div>
            </div>
            <div className="table-row">
              <div className="table-cell description-cell">
                <div className="item-name">{auction.product_name}</div>
                <div className="item-description">
                  {invoice.product_description || auction.product_description || "No description available"}
                </div>
              </div>
              <div className="table-cell quantity-cell">
                {invoice.quantity || auction.quantity || 1}
              </div>
              <div className="table-cell amount-cell">
                {formatCurrency(invoice.unit_price || invoice.subtotal)}
              </div>
            </div>
          </div>

          <div className="divider"></div>

          {/* Summary Section */}
          <div className="summary-section">
            <div className="summary-row">
              <span className="summary-label">Subtotal:</span>
              <span className="summary-value">{formatCurrency(invoice.subtotal)}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">GST ({gstPercentage}):</span>
              <span className="summary-value">{formatCurrency(invoice.gst_amount)}</span>
            </div>
            <div className="summary-row total-row">
              <span className="summary-label">Total Amount:</span>
              <span className="summary-value total-amount">{formatCurrency(invoice.total_amount)}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="btn-download" onClick={handleDownloadInvoice}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Download Invoice
          </button>
          {showProceedToPayment ? (
            <button className="btn-proceed-payment" onClick={handleProceedToPayment}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              Proceed to Payment
            </button>
          ) : null}
          {showEmailToBuyer ? (
            <button className="btn-email" onClick={handleEmailToBuyer} disabled={sendingEmail}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
              {sendingEmail ? 'Sending...' : 'Email to Buyer'}
            </button>
          ) : null}
          {!showProceedToPayment && !showEmailToBuyer && !isQAInvoice ? (
            <p className="small text-muted mb-0 align-self-center">
              Only the winning buyer can proceed to payment. The auction seller can email the invoice to the buyer.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
